import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo, useState } from 'react';

import { PriceOptionDto } from '@cineamo/api/models/price-option/PriceOptionDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconTick16 } from '@cineamo/ui/src/icons/ic-tick';
import { Seat as BaseSeat } from '@cineamo/ui/src/seat-map/typedefs';

import { SelectedSeat, useCartProvider } from '@cineamo/composables/src/providers/cartProvider';
import CartItem from '@cineamo/composables/src/ticket-booking/CartItem';

export interface Seat extends BaseSeat {
    priceOptionId?: PriceOptionDto['id'];
}

const SelectedSeatCartItem: React.FC<{
    className?: string;
    seat: Seat;
    selectedSeat: SelectedSeat;
    isDisabled: boolean;
}> = ({ className, seat, selectedSeat, isDisabled }) => {
    const t = useTranslations('tickets');
    const { applyPriceOption, currency, priceOptions } = useCartProvider();

    const [isProcessing, setIsProcessing] = useState(false);
    const formatter = useFormat();

    const priceOptionsForSeat = useMemo(
        () =>
            priceOptions
                .filter((o) => o._embedded?.seatCategory?.id === seat.category?.id)
                .map((priceOption) => ({
                    ...priceOption,
                    price: priceOption.priceTotal
                })),
        [priceOptions, seat.category?.id]
    );

    const defaultPriceOption = useMemo(
        () => priceOptionsForSeat.find((priceOption) => priceOption.isShowingDefault),
        [priceOptionsForSeat]
    );

    const selectedPriceOption = useMemo(() => {
        const priceOption = priceOptions.find((priceOption) => priceOption.id === selectedSeat.priceOptionId);
        return priceOption ?? defaultPriceOption;
    }, [selectedSeat.priceOptionId, defaultPriceOption, priceOptions]);

    const selectPriceOption = useCallback(
        (priceOption: PriceOptionDto) => {
            setIsProcessing(true);
            applyPriceOption(priceOption.id, selectedSeat.id).finally(() => {
                setIsProcessing(false);
            });
        },
        [applyPriceOption, selectedSeat.id]
    );

    return (
        <CartItem
            className={cn(
                'bg-[linear-gradient(180deg,var(--tw-gradient-stops))] from-[rgba(var(--surface-color-rgb),0.05)] to-[rgba(var(--surface-color-rgb),0.1)]',
                'p-[8px] rounded-4',
                className
            )}
            title={t('selected-seat.title', {
                row: seat.rowName ?? 'N/A',
                seat: seat.positionName ?? 'N/A'
            })}
            subtitle={
                selectedPriceOption?._embedded?.priceDiscount?.name
                    ? `1x ${selectedPriceOption?._embedded?.priceDiscount?.name}`
                    : undefined
            }
            buttonLabel={t('change-price-type')}
            price={selectedPriceOption?.priceTotal ?? undefined}
            currency={currency}
            isDisabled={isDisabled || priceOptionsForSeat.length === 0}
            isProcessing={isProcessing}>
            <div>
                {priceOptionsForSeat.length > 1 && (
                    <div className="py-8">
                        {priceOptionsForSeat.map((priceOption) => (
                            <button
                                key={seat.id + '-' + priceOption.id}
                                className={cn(
                                    'flex flex-row items-center space-x-8 w-full text-left py-6 pl-24',
                                    selectedPriceOption?.id === priceOption.id
                                        ? 'bg-[rgba(var(--primary-color-rgb),0.1)]'
                                        : 'hover:bg-[rgba(var(--primary-color-rgb),0.05)]'
                                )}
                                onClick={() => {
                                    selectPriceOption(priceOption);
                                }}>
                                {selectedPriceOption?.id === priceOption.id && <IconTick16 className="-ml-24" />}
                                <span className="grow text-body-bold-14">
                                    {priceOption._embedded?.priceDiscount?.name}
                                </span>
                                <span className="text-body-bold-16 leading-[28px]">
                                    {formatter.number.formatCurrency((priceOption.priceTotal ?? 0) / 100, currency)}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </CartItem>
    );
};

export default SelectedSeatCartItem;
